import {EventStreamContentType, fetchEventSource} from "@microsoft/fetch-event-source";

/**
 * SSE stream over fetch (not EventSource) so we can read the HTTP status of a
 * failed response in onError.
 *
 * Handlers:
 *   onDelta(delta, data) - a "text_delta" chunk
 *   onEnd(data)          - a "text_end" / "stream_end" chunk
 *   onError({status, event, data, cause}) - status is the HTTP code when the
 *                                           response failed, otherwise null
 */

// used to pass an onError payload back through fetchEventSource
class SseStreamError extends Error {
  constructor(payload) {
    super("sse-stream");
    this.payload = payload;
  }
}

export function useSseStream(url, handlers) {
  const controller = new AbortController();
  let finished = false;

  const run = () =>
    fetchEventSource(url, {
      signal: controller.signal,
      headers: {Accept: "text/event-stream"},
      // one-shot + expensive, so don't let the tab going hidden restart it
      openWhenHidden: true,

      async onopen(response) {
        const contentType = response.headers.get("content-type");
        if (response.ok && contentType?.startsWith(EventStreamContentType)) {
          return;
        }

        // Failed before streaming started - read the JSON error body so a
        // user-facing message (buildFailureResponse) can be surfaced.
        const data = await response.text().catch(() => null);
        throw new SseStreamError({status: response.status, data, cause: response});
      },

      onmessage(message) {
        // terminal markers with no JSON payload ([DONE] is the one this backend sends)
        if (message.event === "done" || message.data === "" || message.data === "[DONE]") {
          return;
        }

        if (message.event === "error") {
          throw new SseStreamError({status: null, event: "error", data: message.data});
        }

        const data = JSON.parse(message.data);

        if (data.type === "text_delta") {
          handlers.onDelta?.(data.delta, data);
        }

        if (data.type === "text_end" || data.type === "stream_end") {
          finished = true;
          handlers.onEnd?.(data);
          controller.abort();
        }
      },

      onclose() {
        // closed before we got an end chunk - the stream was cut off
        if (!finished) {
          throw new SseStreamError({status: null, cause: new Error("stream closed unexpectedly")});
        }
      },

      // never retry - just fail
      onerror(error) {
        throw error;
      },
    });

  void run().catch((error) => {
    if (error?.name === "AbortError") {
      return;
    }

    if (error instanceof SseStreamError) {
      handlers.onError?.(error.payload);
      return;
    }

    handlers.onError?.({status: null, cause: error});
  });

  return {
    close: () => controller.abort(),
  };
}
